import { type SxProps, type Theme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { addDays } from 'date-fns';
import { useCallback, useState } from 'react';

import { content } from './DatePickerWrapper.content';
import { isBeforeMinDate, isNull } from './DatePickerWrapper.utils';

type DatePickerWrapperProps = {
  readonly initialDate?: Date | null;
  readonly label: string;
  readonly minDate?: Date;
  readonly onChange: (date: Date | null) => void;
  readonly sx?: SxProps<Theme>;
};

/**
 * The better DatePicker.
 *
 * Features:
 * - styles the DatePicker to make it obvious to the user that it's required
 * - keeps the user from violating any rules via mouse input
 * - gives helpful information to the user if they violate any rules with keyboard input
 * - enforces a label
 * - is styleable
 * - can take an initialDate
 *
 * Rules:
 * - calls onChange with null if the user empties out the date via keyboard/backspace
 * - calls onChange with null if the chosen date is before the minDate (only if minDate is defined)
 *   - for disabling past dates, just put in `minDate={new Date()}`
 * - calls onChange with chosen date (set to start of the day) if everything's fine and dandy
 */
export const DatePickerWrapper = ({ initialDate, label, minDate, onChange, sx }: DatePickerWrapperProps) => {
  const [date, setDate] = useState(initialDate ?? null);

  const shouldDisableDate = useCallback((passedDate: Date): boolean => isBeforeMinDate(passedDate, minDate), [minDate]);
  const isError = (dateToCheck: Date | null): boolean => isNull(dateToCheck) || isBeforeMinDate(dateToCheck, minDate);

  const handleChange = (value: Date | null): void => {
    setDate(value);

    if (isError(value) || !value) {
      onChange(null);
      return;
    }

    const startOfDayDate = new Date(value);
    startOfDayDate.setHours(0, 0, 0, 0);
    onChange(startOfDayDate);
  };

  const helperText = (): string | undefined => {
    if (isNull(date)) {
      return content.isNullError;
    } else if (isBeforeMinDate(date, minDate)) {
      return content.isBeforeMinDateError(minDate);
    }

    return undefined;
  };

  // open the date picker in the month of the first available date (minDate + 1)
  const referenceDate = (): Date | undefined => {
    if (!minDate) return undefined;
    return addDays(minDate, 1);
  };

  return (
    <DatePicker
      label={label}
      minDate={minDate}
      onChange={handleChange}
      referenceDate={referenceDate()}
      shouldDisableDate={shouldDisableDate}
      slotProps={{
        textField: {
          sx,
          required: true,
          error: isError(date),
          helperText: helperText(),
        },
      }}
      value={date}
      views={['year', 'month', 'day']}
    />
  );
};
